<script>
import debounce from 'lodash/debounce';
import Draggable from 'vuedraggable';
import Selectable from '@/components/selectable/Selectable';
import ImageListItem from '@/components/image-list/ImageListItem';

export default {
  name: 'ImageList',
  components: {
    Draggable,
    ImageListItem,
    Selectable
  },
  props: {
    allowDelete: Boolean,
    allowDrop: Boolean,
    allowSort: Boolean,
    canSelect: {
      type: Function,
      default: () => true
    },
    emptyText: String,
    filterMethod: {
      type: Function,
      default: () => false
    },
    height: {
      type: Number,
      default: 320
    },
    loading: Boolean,
    multiple: Boolean,
    cols: {
      type: Number,
      default: 1,
      validator(val) {
        return [1, 2, 3, 4, 5, 6].includes(val);
      }
    },
    value: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isDragging: false,
      items: [],
      selection: []
    };
  },
  watch: {
    value(val) {
      this.items = val;
    }
  },
  created() {
    this.items = this.value;
  },
  methods: {
    getSelection() {
      return this.selection;
    },

    setSelection(items) {
      this.$refs.selectable.setSelection(items);
    },

    deleteSelection() {
      if (this.allowDelete && this.selection.length) {
        this.items = this.items.filter(item => !this.selection.includes(item));
        this.$emit('input', this.items);
      }
    },

    handleDragOver(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = true;
      }
    },

    handleDragLeave(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = false;
      }
    },

    handleDrop(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = false;
        this.$emit('drop', event);
      }
    },

    handleKeyDown(event) {
      if (['Backspace', 'Delete'].includes(event.key)) {
        this.deleteSelection();
      }
    },

    handleScroll: debounce(
      function () {
        const list = this.$refs.list;
        const listHeight = list.offsetHeight;
        const scrollTop = list.scrollTop;
        const scrollHeight = list.scrollHeight;
        const nearThreshold = listHeight / 2;

        if (scrollTop + listHeight + nearThreshold >= scrollHeight) {
          this.$emit('scroll-near-bottom');
        }

        if (scrollTop + listHeight >= scrollHeight) {
          this.$emit('scroll-at-bottom');
        }
      },
      500,
      { maxWait: 100 }
    ),

    handleSelect(item, event) {
      if (this.canSelect(item)) {
        this.$refs.selectable.select(item, event);
      }
    },

    scrollToTop() {
      const list = this.$refs.list;
      list.scrollTop = 0;
    },

    scrollToSelection() {
      const list = this.$refs.list;
      const min = list.scrollTop;
      const max = list.scrollTop + list.offsetHeight;
      const item = list.querySelector('.is-selected'); // first selected item

      // Only scroll if the selected item is out of view
      if (item && (item.offsetTop < min || item.offsetTop > max)) {
        list.scrollTop = item.offsetTop - list.offsetHeight / 2 + item.offsetHeight / 2;
      }
    }
  }
};
</script>

<template>
  <div
    v-loading="loading"
    :class="{
      'image-list-container': true,
      'is-dragging': isDragging
    }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div
      ref="list"
      :style="`height: ${height}px;`"
      class="image-list"
      @scroll="handleScroll"
      @click="$refs.selectable.selectNone()"
    >
      <selectable
        ref="selectable"
        v-model="selection"
        :items="items"
        :multiple="multiple"
        key-prop="id"
        @input="$emit('select', selection)"
        @keydown.native="handleKeyDown"
      >
        <draggable
          v-model="items"
          :class="`image-cols cols-${cols}`"
          :disabled="!allowSort || items.length === 0"
          :fallback-tolerance="2"
          :force-fallback="true"
          :scroll-sensitivity="45"
          :scroll-speed="15"
          :animation="250"
          @sort="$emit('input', items)"
          @update="$emit('sort', items)"
        >
          <!-- Images -->
          <image-list-item
            v-for="item in items"
            :key="JSON.stringify(item)"
            :hidden="filterMethod(item)"
            :src="item.src"
            :alt="item.alt"
            :info-link="item.infoLink"
            :selected="$refs.selectable.isSelected(item)"
            @click.stop="
              $emit('click', item);
              handleSelect(item, $event);
            "
            @dblclick="$emit('dblclick', item)"
          />
        </draggable>
      </selectable>

      <!-- Empty state -->
      <div v-if="items.length === 0 && emptyText" class="no-items">
        {{ emptyText }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.image-list-container {
  border: solid 1px $--color-gray-20;
  border-radius: 0.25rem;
  background: $--color-white;
  font-size: 1rem;
  line-height: 1.4;
  user-select: none;
  margin-bottom: 1rem;
  transition: 0.1s border, 0.1s background-color;

  &.is-dragging {
    border-color: $--color-primary;
    background-color: $--color-primary-light-9;
  }

  /deep/ .el-loading-mask {
    border-radius: inherit;
    z-index: 0; // allow add/delete buttons to show
  }

  @include breakpoint('sm-and-down') {
    margin-bottom: 0.5rem;
  }
}

.image-list {
  border-radius: inherit;
  overflow: auto;
  padding: 0.25rem;
  margin: 0;
}

.image-cols {
  &.cols-1 .image-list-item {
    width: 100%;
  }

  &.cols-2 .image-list-item {
    width: 50%;
  }

  &.cols-3 .image-list-item {
    width: 33.33%;
  }

  &.cols-4 .image-list-item {
    width: 25%;
  }

  &.cols-5 .image-list-item {
    width: 20%;
  }

  &.cols-6 .image-list-item {
    width: 16.66%;
  }

  .sortable-ghost {
    visibility: hidden;
  }

  .sortable-drag {
    opacity: 1 !important;
  }
}

.no-items {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $--color-gray-40;
  text-align: center;
  word-break: normal;
  padding: 1rem;
  height: 100%;
  width: 100%;
}
</style>
